import React, { useState } from 'react';
import { Zap, Crown, Briefcase, Check, Shield, RotateCcw, Target } from 'lucide-react';
import './PricingPage.css';
import { PLAN_LIMITS, formatDuration } from '../constants/planLimits';
import { useAuth } from '../AuthContext';
import { auth } from '../firebase'; // ✅ Import Firebase auth directly

const PricingPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, showAuthModal } = useAuth();

  const handleUpgrade = async (planKey) => {
    const plan = PLAN_LIMITS[planKey];

    // Check if product ID exists
    if (!plan.productId) {
      alert('This plan is not available for purchase yet. Please check back soon!');
      return;
    }

    // Check if user is authenticated - show auth modal instead of alert
    if (!user) {
      showAuthModal('signup');
      return;
    }

    setIsLoading(true);

    try {
      // ✅ Get ID token directly from Firebase auth.currentUser
      const idToken = await auth.currentUser.getIdToken();

      // Call your backend to create checkout session
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL || 'http://localhost:5000'}/api/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            productId: plan.productId,
            planType: planKey
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Dodo checkout page
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      
      alert(error.message || 'Failed to create checkout session. Please try again.');
      setIsLoading(false);
    }
  };

  // Handle Free Plan - Show auth modal if not logged in
  const handleGetStartedFree = () => {
    if (!user) {
      showAuthModal('signup');
    } else {
      window.location.href = '/generate';
    }
  };

  // Render a plan card (only for paid plans)
  const renderPlanCard = (planKey, plan) => {
    const Icon = planKey === 'starter' ? Zap
                : planKey === 'creator' ? Crown 
                : planKey === 'professional' ? Briefcase 
                : Briefcase;

    const highlights = [
      { label: 'Monthly Projects', value: plan.monthlyProjects.total },
      { label: 'Max Length', value: formatDuration(plan.maxAudioDuration) },
      { label: 'HD Video Downloads', value: plan.mp4Downloads }
    ];

    return (
      <div
        key={planKey}
        className="pricing-card"
      >
        <div className="pricing-card-header">
          <h3 className="pricing-plan-name">
            <Icon size={20} className="pricing-plan-icon" />
            {plan.name}
          </h3>

          <div className="pricing-price-container">
            <span className="pricing-currency">$</span>
            <span className="pricing-amount">{plan.price.toFixed(2)}</span>
            <span className="pricing-period">/month</span>
          </div>
          <p className="plan-description">
            {planKey === 'starter'
              ? 'Perfect for getting started with AI-powered presentations'
              : planKey === 'creator'
              ? 'Essential AI-powered slide generation for content creators'
              : planKey === 'professional'
              ? 'Advanced features for consultants and course creators'
              : 'Essential AI-powered slide generation'
            }
          </p>
        </div>

        <div className="highlights">
          {highlights.map((h, i) => (
            <div key={i} className="highlight-item">
              <span className="highlight-label">{h.label}:</span>
              <span className="highlight-value">{h.value}</span>
            </div>
          ))}
        </div>

        <div className="plan-features">
          <h4 className="plan-features-title">What's Included:</h4>
          <ul className="plan-features-list">
            {plan.features.map((feature, i) => (
              <li key={i} className="plan-feature-item">
                <div className="plan-check-icon">
                  <Check size={12} color="white" strokeWidth={3} />
                </div>
                <span className="plan-feature-text">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-actions">
          <button
            className="pricing-button button-primary"
            disabled={isLoading}
            onClick={() => handleUpgrade(planKey)}
          >
            {isLoading ? 'Processing...' : `Get ${plan.name}`}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1 className="pricing-title">Simple, Transparent Pricing</h1>
        <p className="pricing-subtitle">
          Pay only for what you need. Choose the plan that fits your needs.
        </p>
      </div>

      {/* Free Plan Banner */}
      <div className="free-plan-banner">
        <h3>Start Free</h3>
        <p>
          <strong>{PLAN_LIMITS.free.monthlyProjects.prompt} AI Prompt Projects</strong> and <strong>{PLAN_LIMITS.free.monthlyProjects.audio} Audio Projects</strong> each month. 
          No credit card required.
        </p>
        <button
          className="pricing-button button-free"
          onClick={handleGetStartedFree}
        >
          Get Started Free
        </button>
      </div>

      <div className="pricing-grid">
        {/* Render Only Paid Plans */}
        {renderPlanCard('starter', PLAN_LIMITS.starter)}
        {renderPlanCard('creator', PLAN_LIMITS.creator)}
        {renderPlanCard('professional', PLAN_LIMITS.professional)}
      </div>

      {/* Trust Section */}
      <div className="trust-section">
        <div className="trust-grid">
          <div className="trust-item">
            <div className="trust-icon">
              <Shield size={24} />
            </div>
            <h4>Secure Payments</h4>
            <p>All transactions protected by 256-bit SSL encryption</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">
              <Zap size={24} />
            </div>
            <h4>Instant Activation</h4>
            <p>Your plan upgrades immediately after payment</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">
              <RotateCcw size={24} />
            </div>
            <h4>Cancel Anytime</h4>
            <p>No long-term contracts or hidden fees</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">
              <Target size={24} />
            </div>
            <h4>30-Day Money Back</h4>
            <p>Full refund if you're not satisfied</p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <h3>Frequently Asked Questions</h3>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How does billing work?</h4>
            <p>You're billed monthly on the date you subscribe. All plans renew automatically, but you can cancel anytime before your next billing date.</p>
          </div>
          <div className="faq-item">
            <h4>What happens when I reach my monthly limit?</h4>
            <p>You'll be notified when approaching your limit. Your existing projects remain accessible—you just can't create new ones until you upgrade or your limit resets next month.</p>
          </div>
          <div className="faq-item">
            <h4>Can I change plans later?</h4>
            <p>Yes. Upgrade anytime with immediate effect. Downgrades take effect at your next billing cycle, and you keep your current features until then.</p>
          </div>
          <div className="faq-item">
            <h4>What export formats are supported?</h4>
            <p>All plans include unlimited PNG and PPTX exports. Paid plans also include MP4 video downloads with monthly limits based on your tier.</p>
          </div>
          <div className="faq-item">
            <h4>Do you offer refunds?</h4>
            <p>Yes. We offer a 30-day money-back guarantee on all plans. Contact support within 30 days of purchase for a full refund.Contact@cayoty.com</p>
          </div>
          <div className="faq-item">
            <h4>Are there any additional fees?</h4>
            <p>No. The price you see is the price you pay. No setup fees, no transaction fees, no hidden costs.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;